// src/pages/SmsBotManagement.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Alert,
  Pagination,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  PhoneAndroid as PhoneIcon,
  PhoneDisabled as DisconnectIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

// Country codes with flags (using emoji flags)
const COUNTRY_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+82', country: 'KR', flag: '🇰🇷', name: 'South Korea' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+92', country: 'PK', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+971', country: 'AE', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia' }
];

const SmsBotConnector = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  
  // Bot connection state
  const [activeSessions, setActiveSessions] = useState([]);
  const [connectDialog, setConnectDialog] = useState({ open: false });
  const [connectForm, setConnectForm] = useState({ 
    country_code: '+1',
    phone_number: '',
    full_phone_number: ''
  });
  const [phoneError, setPhoneError] = useState('');
  
  // Sessions history state
  const [sessions, setSessions] = useState([]);
  const [sessionsPagination, setSessionsPagination] = useState({ page: 1, total_pages: 1 });
  
  // Service configuration state
  const [serviceConfig, setServiceConfig] = useState({
    configured: false,
    service_type: null
  });

  const { chatbotId : paramsChatbotId } = useParams();

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('channels', 'view');
        const modifyPerm = await hasPermission('channels', 'add|edit');
        const deletePerm = await hasPermission('channels', 'delete');

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  // Phone number validation function
  const validatePhoneNumber = (countryCode, phoneNumber) => {
    if (!phoneNumber) {
      return 'Phone number is required';
    }

    const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    if (!/^\d+$/.test(cleanNumber)) {
      return 'Phone number must contain only digits';
    }

    const validationRules = {
      '+1': { min: 10, max: 10 },
      '+44': { min: 10, max: 11 },
      '+33': { min: 9, max: 10 },
      '+49': { min: 10, max: 12 },
      '+39': { min: 9, max: 11 },
      '+34': { min: 9, max: 9 },
      '+91': { min: 10, max: 10 },
      '+86': { min: 11, max: 11 },
      '+81': { min: 10, max: 11 },
      '+82': { min: 9, max: 10 },
      '+61': { min: 9, max: 9 },
      '+92': { min: 10, max: 10 },
      '+971': { min: 8, max: 9 },
      '+966': { min: 9, max: 9 }
    };

    const rule = validationRules[countryCode];
    if (rule) {
      if (cleanNumber.length < rule.min || cleanNumber.length > rule.max) {
        return `Phone number must be ${rule.min}${rule.min !== rule.max ? `-${rule.max}` : ''} digits for ${countryCode}`;
      }
    } else {
      if (cleanNumber.length < 7 || cleanNumber.length > 15) {
        return 'Phone number must be between 7-15 digits';
      }
    }

    return '';
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    if (!phone.startsWith('+')) {
      return phone;
    }
    
    let countryCode = '';
    let numberPart = '';
    
    for (let i = 2; i <= 5 && i <= phone.length; i++) {
      const potentialCode = phone.substring(0, i);
      if (/^\+\d+$/.test(potentialCode)) {
        const isKnownCode = COUNTRY_CODES.some(c => c.code === potentialCode);
        if (isKnownCode || i === 5) {
          countryCode = potentialCode;
          numberPart = phone.substring(i);
          break;
        }
      }
    }
    
    if (!countryCode) {
      const match = phone.match(/^(\+\d{1,4})(.*)$/);
      if (match) {
        [, countryCode, numberPart] = match;
      } else {
        return phone;
      }
    }
    
    const cleanNumber = numberPart.replace(/[\s\-\(\)]/g, '');
    
    if (countryCode === '+1' && cleanNumber.length === 10) {
      return `${countryCode} (${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6)}`;
    }
    
    if (cleanNumber.length >= 8) {
      const formatted = cleanNumber.replace(/(\d{3,4})(?=\d)/g, '$1 ');
      return `${countryCode} ${formatted}`;
    }
    
    return `${countryCode} ${cleanNumber}`;
  };

  // Load initial data
  useEffect(() => {
    if (canView) {
      loadServiceStatus();
      loadActiveSessions();
      loadSessionsHistory();
    }
  }, [canView]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadServiceStatus = async () => {
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get('/sms/service/status', { params });
      setServiceConfig(response.data.data);
    } catch (error) {
      console.error('Error loading service status:', error);
      showSnackbar('Could not load SMS service status.', 'error');
    }
  };

  const loadActiveSessions = async () => {
    try {
        const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
        const response = await axiosInstance.get('/sms-bot/status', { params });
        setActiveSessions(response.data.data.active_sessions);
    } catch (error) {
      console.error('Error loading active sessions:', error);
      showSnackbar('Error loading active sessions', 'error');
    }
  };

  const loadSessionsHistory = async (page = 1) => {
    try {

      const params = { page }; // always include page param

      if (paramsChatbotId) {
        params.chatbot_id = paramsChatbotId; // conditionally add chatbot_id
      }

      const response = await axiosInstance.get('/sms-bot/sessions', { params });
      setSessions(response.data.data.sessions);
      setSessionsPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error loading sessions:', error);
      showSnackbar('Error loading sessions history', 'error');
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const cleanValue = value.replace(/[^\d\s\-\(\)]/g, '');
    
    setConnectForm(prev => ({
      ...prev,
      phone_number: cleanValue,
      full_phone_number: prev.country_code + cleanValue.replace(/[\s\-\(\)]/g, '')
    }));
    
    const error = validatePhoneNumber(connectForm.country_code, cleanValue);
    setPhoneError(error);
  };

  const handleCountryCodeChange = (e) => {
    const newCountryCode = e.target.value;
    setConnectForm(prev => ({
      ...prev,
      country_code: newCountryCode,
      full_phone_number: newCountryCode + prev.phone_number.replace(/[\s\-\(\)]/g, '')
    }));
    
    if (connectForm.phone_number) {
      const error = validatePhoneNumber(newCountryCode, connectForm.phone_number);
      setPhoneError(error);
    }
  };

  const checkForActiveSession = (phoneNumber) => {
    return activeSessions.some(session => 
      session.phone_number === phoneNumber && session.status === 'connected'
    );
  };

  const handleConnect = async () => {
    const phoneValidationError = validatePhoneNumber(connectForm.country_code, connectForm.phone_number);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      showSnackbar('Please fix the phone number format', 'error');
      return;
    }

    if (checkForActiveSession(connectForm.full_phone_number)) {
      showSnackbar('This phone number already has an active SMS bot session. Please disconnect the existing session first.', 'error');
      return;
    }

    try {
      setLoading(true);

      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

      const response = await axiosInstance.post('/sms-bot/connect', {
        phone_number: connectForm.full_phone_number
      }, { params });
      
      if (response.data.success) {
        showSnackbar('SMS bot connected successfully!', 'success');
        setConnectDialog({ open: false });
        setConnectForm({ 
          country_code: '+1',
          phone_number: '',
          full_phone_number: ''
        });
        setPhoneError('');
        loadActiveSessions();
        loadSessionsHistory();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error connecting SMS bot', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (phoneNumber) => {
    if (!window.confirm('Are you sure you want to disconnect this SMS bot?')) return;

    try {
      setLoading(true);

      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.post('/sms-bot/disconnect', {
        phone_number: phoneNumber
      },{ params });
      
      if (response.data.success) {
        showSnackbar('SMS bot disconnected successfully!', 'success');
        loadActiveSessions();
        loadSessionsHistory();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error disconnecting SMS bot', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      connected: 'success',
      disconnected: 'default',
      error: 'error'
    };
    return colors[status] || 'default';
  };

  const getSelectedCountryFlag = () => {
    const country = COUNTRY_CODES.find(c => c.code === connectForm.country_code);
    return country ? country.flag : '🌍';
  };

  const resetDialog = () => {
    setConnectDialog({ open: false });
    setPhoneError('');
    setConnectForm({ 
      country_code: '+1',
      phone_number: '',
      full_phone_number: ''
    });
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <Typography>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ width: '100%', p: 3, mt: { xs: 0, md: 3 } }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view SMS Bot Management. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3, mt: { xs: 0, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        SMS Bot Management
      </Typography>

      {!serviceConfig.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          SMS service is not configured. Contact administrator to enable SMS bot functionality.
        </Alert>
      )}

      {/* Quick Actions Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1 }} />
                Connect SMS Bot
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your SMS bot to a phone number and start chatting with AI assistance.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setConnectDialog({ open: true })}
                disabled={!serviceConfig.configured || activeSessions.length > 0 || !canModify}
                fullWidth
              >
                Connect New Number
              </Button>
              {activeSessions.length > 0 && canModify && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Disconnect active sessions to connect a new number
                </Typography>
              )}
              {!canModify && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  You don't have permission to connect new numbers
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatIcon sx={{ mr: 1 }} />
                Active Sessions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {activeSessions.length} active SMS bot sessions running
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadActiveSessions}
                fullWidth
              >
                Refresh Status
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Sessions Table */}
      {activeSessions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1 }} />
            Active SMS Bot Sessions
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Website Alias</TableCell>
                  <TableCell>Connected At</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Status</TableCell>
                  {canModify && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeSessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>{formatPhoneNumber(session.phone_number)}</TableCell>
                    <TableCell>{session.website_alias || 'N/A'}</TableCell>
                    <TableCell>
                      {new Date(session.connected_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {session.last_activity_at 
                        ? new Date(session.last_activity_at).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={session.status} 
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    {canModify && (
                      <TableCell>
                        <IconButton
                          onClick={() => handleDisconnect(session.phone_number)}
                          color="error"
                          disabled={loading}
                          title="Disconnect SMS Bot"
                        >
                          <DisconnectIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Sessions History */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1 }} />
          SMS Bot Sessions History
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Website Alias</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Connected At</TableCell>
                <TableCell>Disconnected At</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => {
                const duration = session.connected_at && session.disconnected_at
                  ? Math.round((new Date(session.disconnected_at) - new Date(session.connected_at)) / 1000 / 60)
                  : null;

                return (
                  <TableRow key={session.id}>
                    <TableCell>{formatPhoneNumber(session.phone_number)}</TableCell>
                    <TableCell>{session.website_alias || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={session.status} 
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {session.connected_at 
                        ? new Date(session.connected_at).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {session.disconnected_at 
                        ? new Date(session.disconnected_at).toLocaleString()
                        : session.status === 'connected' ? 'Active' : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {duration ? `${duration} min` : session.status === 'connected' ? 'Active' : 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {sessionsPagination.total_pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={sessionsPagination.total_pages}
              page={sessionsPagination.page}
              onChange={(e, page) => loadSessionsHistory(page)}
            />
          </Box>
        )}
      </Box>

      {/* Connect Dialog */}
      <Dialog 
        open={connectDialog.open} 
        onClose={resetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect SMS Bot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter a phone number to connect your SMS bot. The bot will send a welcome message and be ready to chat.
          </Typography>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Country</InputLabel>
            <Select
              value={connectForm.country_code}
              label="Country"
              onChange={handleCountryCodeChange}
              renderValue={(selected) => {
                const country = COUNTRY_CODES.find(c => c.code === selected);
                return country ? `${country.flag} ${country.code} ${country.name}` : selected;
              }}
            >
              {COUNTRY_CODES.map((country, index) => (
                <MenuItem 
                  key={`${country.code}-${country.country}-${index}`} 
                  value={country.code}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <span style={{ fontSize: '1.2em' }}>{country.flag}</span>
                  <span>{country.code}</span>
                  <span>{country.name}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Phone Number"
            value={connectForm.phone_number}
            onChange={handlePhoneNumberChange}
            margin="normal"
            required
            error={!!phoneError}
            helperText={phoneError || "Enter phone number without country code"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: '1.1em' }}>{getSelectedCountryFlag()}</span>
                    <span style={{ fontWeight: 'bold' }}>{connectForm.country_code}</span>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="1234567890"
          />

          {activeSessions.length > 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Active Session Detected:</strong> You currently have {activeSessions.length} active SMS bot session{activeSessions.length > 1 ? 's' : ''}. 
              Please disconnect the existing session{activeSessions.length > 1 ? 's' : ''} before connecting a new number.
              <br /><br />
              <strong>Active Numbers:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {activeSessions.map((session, index) => (
                  <li key={session.id}>{formatPhoneNumber(session.phone_number)}</li>
                ))}
              </ul>
            </Alert>
          ) : (
            connectForm.full_phone_number && !phoneError && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Full Number:</strong> {formatPhoneNumber(connectForm.full_phone_number)}
                <br />
                The bot will send a welcome message to this number and be ready to assist with questions.
              </Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            variant="contained"
            disabled={loading || !connectForm.phone_number || !!phoneError}
          >
            {loading ? <CircularProgress size={20} /> : 'Connect Bot'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity || 'info'} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmsBotConnector;